import React, { useEffect, useRef, useState } from 'react';

/**
 * TreeNodeMenu - Context menu for family tree nodes
 *
 * Shows actions when clicking on a person in the tree:
 * - Add Spouse, Add Child, Add Sibling, Add Parent
 * - Edit, View
 */
function TreeNodeMenu({
  isOpen,
  onClose,
  position,
  personName,
  personId,
  hasSpouse,
  onAddRelative,
  onEdit,
  onView
}) {
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // Calculate menu position to keep it within viewport
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let x = position.x;
      let y = position.y;

      // Adjust horizontal position if menu goes off-screen
      if (x + menuRect.width / 2 > viewportWidth - 20) {
        x = viewportWidth - menuRect.width / 2 - 20;
      }
      if (x - menuRect.width / 2 < 20) {
        x = menuRect.width / 2 + 20;
      }

      // Adjust vertical position - show above if not enough space below
      if (y + menuRect.height > viewportHeight - 20) {
        y = position.y - menuRect.height - 10;
      }

      setMenuPosition({ x, y });
    }
  }, [isOpen, position]);

  // Close menu on scroll
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => onClose();
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [isOpen, onClose]);

  // Close menu on Escape key
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const menuItems = [
    {
      id: 'spouse',
      label: 'Add Spouse',
      icon: '💑',
      description: 'Add husband or wife',
      onClick: () => {
        onAddRelative(personId, 'spouse', personName);
        onClose();
      }
    },
    {
      id: 'child',
      label: 'Add Child',
      icon: '👶',
      description: hasSpouse ? 'Add son or daughter to both parents' : 'Add son or daughter',
      onClick: () => {
        onAddRelative(personId, 'child', personName);
        onClose();
      }
    },
    {
      id: 'sibling',
      label: 'Add Sibling',
      icon: '👫',
      description: 'Add brother or sister',
      onClick: () => {
        onAddRelative(personId, 'sibling', personName);
        onClose();
      }
    },
    {
      id: 'parent',
      label: 'Add Parent',
      icon: '👨‍👩‍👦',
      description: 'Add mother or father',
      onClick: () => {
        onAddRelative(personId, 'parent', personName);
        onClose();
      }
    }
  ];

  // Handle click outside to close menu
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop - click to close */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        style={{ background: 'transparent' }}
      />

      {/* Menu positioned near the clicked node */}
      <div
        ref={menuRef}
        className="fixed bg-white rounded-xl shadow-2xl border-2 border-gray-200 overflow-hidden z-50"
        style={{
          left: menuPosition.x || position.x,
          top: menuPosition.y || position.y,
          minWidth: '280px',
          transform: 'translate(-50%, 0)',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#556B2F] text-white px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">{personName}</h3>
              <p className="text-sm opacity-90">Add a relative</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Menu Items */}
        <div className="p-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className="w-full flex items-center gap-4 px-4 py-4 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <span className="text-3xl">{item.icon}</span>
              <div className="flex-1">
                <div className="text-lg font-semibold text-gray-800">{item.label}</div>
                <div className="text-sm text-gray-500">{item.description}</div>
              </div>
              <span className="text-2xl text-gray-300">›</span>
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mx-4"></div>

        {/* Secondary Actions */}
        <div className="p-2">
          {onView && (
            <button
              onClick={() => {
                onView(personId);
                onClose();
              }}
              className="w-full flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <span className="text-2xl">👁️</span>
              <span className="text-base font-medium text-gray-700">View Profile</span>
            </button>
          )}
          {onEdit && (
            <button
              onClick={() => {
                onEdit(personId);
                onClose();
              }}
              className="w-full flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <span className="text-2xl">✏️</span>
              <span className="text-base font-medium text-gray-700">Edit Details</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default TreeNodeMenu;
